import type { UploadResponse } from '@/types/upload'
import { getIdToken, requireLogin } from '@/auth/session'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface UploadDocumentParams {
  file: File
  businessId: string
  forceReprocess?: boolean
  onProgress?: (progress: number) => void
}

export class UploadError extends Error {
  status: number
  // Machine-readable error code from the API body (e.g. 'VALIDATED_IMMUTABLE'), when present.
  code?: string

  constructor(status: number, message: string, code?: string) {
    super(message)
    this.name = 'UploadError'
    this.status = status
    this.code = code
  }
}

/**
 * Upload a document to the expenses API.
 * Uses XMLHttpRequest for progress tracking support.
 */
export async function uploadDocument(params: UploadDocumentParams): Promise<UploadResponse> {
  const token = await getIdToken()
  if (!token) {
    await requireLogin()
    throw new UploadError(401, 'Sign-in required')
  }
  try {
    return await sendUpload(params, token)
  } catch (error) {
    if (!(error instanceof UploadError) || error.status !== 401) throw error
    const refreshed = await getIdToken(true)
    if (refreshed) {
      try {
        return await sendUpload(params, refreshed)
      } catch (retryError) {
        if (retryError instanceof UploadError && retryError.status === 401) {
          throw new UploadError(401, 'Sign-in required', 'UNAUTHORIZED')
        }
        throw retryError
      }
    }
    await requireLogin()
    throw new UploadError(401, 'Sign-in required')
  }
}

function sendUpload(params: UploadDocumentParams, token: string): Promise<UploadResponse> {
  const { file, businessId, forceReprocess, onProgress } = params
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const formData = new FormData()
    
    formData.append('file', file)
    formData.append('business_id', businessId)
    
    if (forceReprocess) {
      formData.append('force_reprocess', 'true')
    }
    
    // Track upload progress
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100)
        onProgress(progress)
      }
    })
    
    // Handle successful response
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText) as UploadResponse
          resolve(response)
        } catch {
          reject(new UploadError(xhr.status, 'Invalid response format'))
        }
      } else {
        let errorMessage = `Upload failed with status ${xhr.status}`
        let errorCode: string | undefined
        try {
          const errorResponse = JSON.parse(xhr.responseText)
          if (errorResponse?.error?.message) {
            errorMessage = errorResponse.error.message
          } else if (typeof errorResponse?.message === 'string') {
            errorMessage = errorResponse.message
            errorCode = `HTTP_${xhr.status}`
          }
          if (errorResponse?.error?.code) {
            errorCode = errorResponse.error.code
          }
        } catch {
          // Use default error message
        }
        reject(new UploadError(xhr.status, errorMessage, errorCode))
      }
    })
    
    // Handle network errors
    xhr.addEventListener('error', () => {
      reject(new UploadError(0, 'Network error during upload'))
    })
    
    // Handle timeout
    xhr.addEventListener('timeout', () => {
      reject(new UploadError(0, 'Upload timed out'))
    })
    
    // Handle abort
    xhr.addEventListener('abort', () => {
      reject(new UploadError(0, 'Upload was cancelled'))
    })
    
    // Configure and send request
    xhr.open('POST', `${API_BASE_URL}/upload/document`)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.timeout = 300000 // 5 minute timeout for large files
    xhr.send(formData)
  })
}
